/* eslint-disable no-param-reassign */
import * as utils from '../../../../util';
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicBubbleLayerSpec } from '../../../../layer/preset/scatter/dynamicBubble';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';
import { getDynamicBubbleAdapters } from './dynamicBubbleAdapters';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface DynamicBubbleLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type DynamicBubbleLayerType = StandardChartLayerSpec &
  DynamicBubbleLayerSpec &
  DynamicBubbleLayerTypeWithFields;

/**
 * 创建动态气泡图数据类型验证器
 * @param layerType - 图层类型
 * @param fieldValidators - 字段验证配置
 * @returns 验证函数
 */
const validateDynamicBubbleDataTypes = createDataTypeValidator('dynamicBubble', [
  { field: 'x', type: 'number' },
  { field: 'y', type: 'number' },
  { field: 'color', type: 'string' },
  { field: 'group', type: ['string', 'date'] },
  { field: 'size', type: 'number', required: false }
]);

/**
 * 创建动态气泡图编码验证器
 * @param requiredFields - 必需字段配置
 * @returns 验证函数
 */
const validateDynamicBubbleEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'color' },
  { field: 'group' },
  { field: 'size', required: false }
]);

/**
 * 额外检查函数
 * @param layer - 图层配置
 * @param data - 数据
 */
const additionalDynamicBubbleChecks = (layer: DynamicBubbleLayerType, data: Data) => {
  const { dataIndex = 0, encoding } = layer;
  const rawData = data[dataIndex]?.values || [];

  // 检查总数据量
  if (rawData.length < 3) {
    utils.logger.warn('warning', `The "${layer.type}" layer requires at least 3 data points`);
    return;
  }

  // 获取不同的分组值
  const transformedData = utils.dataTransform.transform(rawData.slice(), [
    { type: 'deDuplication', field: encoding.group },
    { type: 'sort', field: encoding.group, order: 'ascending' }
  ]) as { [key: string]: string | number }[];

  const { dates } = utils.dataEncoding.encoding(transformedData, {
    dates: { field: encoding.group }
  });

  // 检查分组数量
  if (dates.length < 2) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires at least 2 different group values`
    );
    return;
  }

  // 验证每个分组中的数据
  dates.forEach(date => {
    const dateData = rawData.filter(item => item[encoding.group] === date);

    // 检查每个分组的数据量
    if (dateData.length < 3) {
      utils.logger.warn(
        'warning',
        `Each group in "${layer.type}" layer should have at least 3 data points`
      );
    }

    // 检查颜色值的唯一性
    const colorValues = dateData.map(item => item[encoding.color]);
    if (colorValues.length !== new Set(colorValues).size) {
      utils.logger.warn(
        'warning',
        `The "${encoding.color}" field values must be unique within each "${encoding.group}" in "${layer.type}" layer`
      );
    }
  });
};

// 创建并导出动态气泡图处理器
export const dynamicBubble = createLayerProcessor<DynamicBubbleLayerType>(
  'dynamicBubble',
  validateDynamicBubbleEncoding,
  validateDynamicBubbleDataTypes,
  additionalDynamicBubbleChecks,
  getDynamicBubbleAdapters()
);
